import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { API, authFetch } from "@/App";
import {
  Users, Briefcase, RefreshCw, Shield, UserCheck, AlertTriangle,
  ArrowRight, CheckCircle, Loader2, BarChart3, ChevronDown, ChevronRight
} from "lucide-react";

export function AdminPanel({ user }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [selectedConsultant, setSelectedConsultant] = useState("");
  
  const isSuperadmin = user?.is_superadmin || 
    user?.email === "jamilah@mybizconsulting.net" || 
    user?.email === "consultant@mybiz.com";

  useEffect(() => {
    if (isSuperadmin) {
      fetchStats();
      fetchConsultants();
    }
  }, [isSuperadmin]);

  const fetchStats = async () => {
    try {
      const res = await authFetch(`${API}/cases/admin/stats`);
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (_error) {
      console.error("Error fetching admin stats:", _error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConsultants = async () => {
    try {
      const res = await authFetch(`${API}/cases/consultants/list`);
      if (res.ok) {
        setConsultants(await res.json());
      }
    } catch (_error) {
      console.error("Error fetching consultants:", _error);
    }
  };

  const handleSyncClients = async () => {
    setSyncing(true);
    try {
      const res = await authFetch(`${API}/cases/sync-clients`, { method: "POST" });
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message);
        fetchStats();
      } else {
        toast.error(data.detail || "Sync failed");
      }
    } catch (_error) {
      toast.error("Failed to sync clients");
    } finally {
      setSyncing(false);
    }
  };

  const handleReassignCase = async () => {
    if (!selectedCase || !selectedConsultant) return;
    
    try {
      const res = await authFetch(`${API}/cases/admin/reassign/${selectedCase.case_id}`, {
        method: "PUT",
        body: JSON.stringify({ consultant_id: selectedConsultant })
      });
      
      if (res.ok) {
        toast.success("Case reassigned successfully");
        setShowReassignDialog(false);
        setSelectedCase(null);
        setSelectedConsultant("");
        fetchStats();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to reassign case");
      }
    } catch (_error) {
      toast.error("Failed to reassign case");
    }
  };

  if (!isSuperadmin) {
    return null;
  }

  if (loading) {
    return (
      <Card className="mb-6 border-purple-200 bg-purple-50/50">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-6 border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setCollapsed(c => !c)}
              className="flex items-center gap-2 group cursor-pointer"
              data-testid="admin-panel-toggle"
            >
              {collapsed ? (
                <ChevronRight className="w-4 h-4 text-purple-500 group-hover:text-purple-700 transition-colors" />
              ) : (
                <ChevronDown className="w-4 h-4 text-purple-500 group-hover:text-purple-700 transition-colors" />
              )}
              <Shield className="w-5 h-5 text-purple-600" />
              <CardTitle className="text-lg text-purple-900">Admin Panel</CardTitle>
              <Badge className="bg-purple-100 text-purple-700 text-xs">Superadmin</Badge>
              {collapsed && stats && (
                <span className="text-xs text-purple-500 ml-2">
                  {stats?.users?.total || 0} users &middot; {stats?.cases?.total || 0} cases
                </span>
              )}
            </button>
            {!collapsed && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchStats}
                className="border-purple-300 text-purple-700 hover:bg-purple-100"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            )}
          </div>
          {!collapsed && <CardDescription>Full system oversight and management</CardDescription>}
        </CardHeader>
        {!collapsed && (
        <CardContent className="space-y-4" data-testid="admin-panel-content">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white p-3 rounded-lg border border-purple-100">
              <div className="flex items-center gap-2 text-purple-600 mb-1">
                <Users className="w-4 h-4" />
                <span className="text-xs font-medium">Total Users</span>
              </div>
              <p className="text-2xl font-bold text-purple-900">{stats?.users?.total || 0}</p>
              <p className="text-xs text-purple-600">
                {stats?.users?.clients || 0} clients · {stats?.users?.consultants || 0} consultants
              </p>
            </div>
            
            <div className="bg-white p-3 rounded-lg border border-purple-100">
              <div className="flex items-center gap-2 text-purple-600 mb-1">
                <Briefcase className="w-4 h-4" />
                <span className="text-xs font-medium">Total Cases</span>
              </div>
              <p className="text-2xl font-bold text-purple-900">{stats?.cases?.total || 0}</p>
              <p className="text-xs text-purple-600">
                {stats?.cases?.open || 0} open · {stats?.cases?.closed || 0} closed
              </p>
            </div>
            
            <div className="bg-white p-3 rounded-lg border border-purple-100">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <UserCheck className="w-4 h-4" />
                <span className="text-xs font-medium">Clients Synced</span>
              </div>
              <p className="text-2xl font-bold text-green-700">{stats?.sync_status?.clients_with_cases || 0}</p>
              <p className="text-xs text-green-600">All clients in CRM</p>
            </div>
            
            <div className="bg-white p-3 rounded-lg border border-purple-100">
              <div className="flex items-center gap-2 text-amber-600 mb-1">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-xs font-medium">Need Sync</span>
              </div>
              <p className="text-2xl font-bold text-amber-700">
                {Math.max(0, stats?.sync_status?.clients_without_cases || 0)}
              </p>
              {stats?.sync_status?.sync_needed ? (
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="text-xs h-6 px-2 text-amber-700"
                  onClick={handleSyncClients}
                  disabled={syncing}
                >
                  {syncing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  Sync Now
                </Button>
              ) : (
                <p className="text-xs text-green-600">✓ All synced</p>
              )}
            </div>
          </div>

          {/* Consultant Workload */}
          {stats?.consultant_workload && stats.consultant_workload.length > 0 && (
            <div className="bg-white p-4 rounded-lg border border-purple-100">
              <h4 className="text-sm font-semibold text-purple-900 mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Consultant Workload
              </h4>
              <div className="space-y-3">
                {stats.consultant_workload.map((consultant) => (
                  <div key={consultant.user_id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="text-sm font-semibold text-purple-700">
                          {consultant.name?.charAt(0) || "?"}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-neutral-900 flex items-center">
                          {consultant.name}
                          {consultant.is_superadmin && (
                            <Badge className="ml-2 bg-purple-100 text-purple-700 text-[10px]">Admin</Badge>
                          )}
                        </div>
                        <p className="text-xs text-neutral-500">{consultant.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-neutral-900">{consultant.total_cases} cases</p>
                      <p className="text-xs text-amber-600">{consultant.open_cases} open</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleSyncClients}
              disabled={syncing}
              className="bg-purple-600 hover:bg-purple-700"
              size="sm"
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sync All Clients to CRM
            </Button>
          </div>
        </CardContent>
        )}
      </Card>

      {/* Reassign Dialog */}
      <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Case</DialogTitle>
            <DialogDescription>
              Select a consultant to reassign this case to.
            </DialogDescription>
          </DialogHeader>
          
          {selectedCase && (
            <div className="py-4">
              <p className="text-sm text-neutral-600 mb-4">
                Case: <strong>{selectedCase.title}</strong>
              </p>
              <Select value={selectedConsultant} onValueChange={setSelectedConsultant}>
                <SelectTrigger>
                  <SelectValue placeholder="Select consultant" />
                </SelectTrigger>
                <SelectContent>
                  {consultants.map((c) => (
                    <SelectItem key={c.user_id} value={c.user_id}>
                      {c.name} ({c.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReassignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleReassignCase} disabled={!selectedConsultant}>
              Reassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
